import React, { ReactNode, useState, useEffect } from 'react';
import {
  FloatingOverlay,
  FloatingPortal,
  FloatingFocusManager,
  useInteractions,
  useDismiss,
  useFloating,
  useRole,
  useFloatingNodeId,
  useFloatingTree,
  FloatingNode,
  OpenChangeReason,
  FloatingTreeType,
} from '@floating-ui/react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import classNames from 'classnames';
import { AnimatePresence, motion, useDragControls, PanInfo } from 'framer-motion';
import { BaseText } from '../text';
import { colors, semantic_colors, shapes, border_radius_styles_with_size } from '../../foundation';
import { absolute_center, getZIndex, safeAreaInsetBottom, SpaceProps, space, buildCSSWithLength } from '../../styles';
import { Stack } from '../stack';
import { FLOATING_ROOT_ID, FLOATING_ORDER } from '../../constants';
import { useResize } from '../../hooks/useResize';
import { FloatingTreeContext } from '../context';
import { useManualDismiss } from '../modal/useManualDismiss';

export type BottomSheetContentWithCloseAction = (action: { close(): void }) => ReactNode;
export interface BottomSheetContentProps extends SpaceProps {
  maxHeight?: number;
}

export interface BottomSheetProps {
  /** @default false */
  noUsePortal?: boolean;
  className?: string;
  opened?: boolean;
  /** @default getZIndex('bottomSheet') */
  zIndex?: number;
  title?: ReactNode;
  subTitle?: ReactNode;
  children?: ReactNode | BottomSheetContentWithCloseAction;
  contentProps?: BottomSheetContentProps;
  /**
   * outside 영역 클릭시 닫힘 사용 여부
   * @default true
   */
  canClickOutside?: boolean;
  /**
   * escapeKey 키이벤트 닫힘 사용 여부
   * @default true
   */
  canCloseEscapeKey?: boolean;
  /**
   * 드래그 닫힘 사용 여부
   * @default true
   */
  canDragClose?: boolean;
  onClose?: () => void;
}

export const MIN_BOTTOM_SHEET_Y = 44;
export const HANDLE_AREA_HEIGHT = 28;
const MotionFloatingOverlay = motion(FloatingOverlay);

const BottomSheetInner = ({
  noUsePortal = false,
  className,
  zIndex = getZIndex('bottomSheet'),
  opened: openProp,
  canClickOutside = true,
  canCloseEscapeKey = true,
  canDragClose = true,
  title,
  subTitle,
  children,
  contentProps,
  onClose,
}: BottomSheetProps) => {
  const nodeId = useFloatingNodeId();
  const tree = useFloatingTree();
  const { windowHeight } = useResize();
  const [opened, setOpened] = useState(openProp);
  const [maxHeight, setMaxHeight] = useState(() => contentProps?.maxHeight ?? window.innerHeight - MIN_BOTTOM_SHEET_Y);
  const dragControls = useDragControls();
  const containerPosition = noUsePortal ? 'absolute' : 'fixed';
  const floatingOverlayId = `${FLOATING_ROOT_ID}-overlay-${nodeId}`;

  const { refs, context } = useFloating({
    open: opened,
    nodeId,
  });

  const { getFloatingProps } = useInteractions([
    useRole(context, { role: 'dialog' }),
    useDismiss(context, {
      escapeKey: canCloseEscapeKey,
      outsidePress: canClickOutside,
      outsidePressEvent: 'mousedown',
      bubbles: false,
    }),
  ]);

  const handleClose = () => {
    onClose?.();
    setOpened(false);
  };

  const handleDragEnd = (event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    if (!canDragClose) return;
    info.velocity.y > HANDLE_AREA_HEIGHT / 2 && handleClose();
  };

  useEffect(() => {
    if (openProp !== undefined) {
      setOpened(openProp);
    }
  }, [openProp]);

  useEffect(() => {
    if (opened !== undefined && !opened) {
      onClose?.();
    }
  }, [opened]);

  useEffect(() => {
    setMaxHeight(contentProps?.maxHeight ?? window.innerHeight - MIN_BOTTOM_SHEET_Y);
  }, [windowHeight, contentProps?.maxHeight]);

  const handleManualDismiss = (reason: OpenChangeReason) => {
    const reasonMap: Record<OpenChangeReason, string | undefined> = {
      ['outside-press']: 'overlayClick',
      ['escape-key']: 'escapeKeyPress',
      'ancestor-scroll': undefined,
      'click': undefined,
      'focus': undefined,
      'hover': undefined,
      'list-navigation': undefined,
      'reference-press': undefined,
      'safe-polygon': undefined,
    };
    const convertCancelEvent = reasonMap[reason];

    convertCancelEvent && handleClose?.();
  };
  useManualDismiss({
    controlled: true,
    rootId: noUsePortal ? floatingOverlayId : FLOATING_ROOT_ID,
    tree: tree as FloatingTreeType,
    context,
    callbackTypeWithFn: handleManualDismiss,
  });

  const renderToBottomSheet = () => {
    return (
      <MotionFloatingOverlay
        id={floatingOverlayId}
        lockScroll
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        style={{ position: containerPosition, background: semantic_colors.background.scrim, zIndex }}
      >
        <ContentContainer
          drag={canDragClose ? 'y' : false}
          dragControls={dragControls}
          dragListener={false}
          onDragEnd={handleDragEnd}
          dragElastic={0}
          dragConstraints={{ top: 0, bottom: 0 }}
          initial={{ translateY: '100%' }}
          animate={{ translateY: 0 }}
          exit={{ translateY: '100%' }}
          transition={{ ease: 'easeIn', duration: 0.2 }}
          css={css`
            ${safeAreaInsetBottom()}
            max-height: ${maxHeight}px;
            &:focus {
              outline: none;
            }
          `}
          aria-modal
          {...getFloatingProps({
            className: classNames('pds-bottom-sheet', className),
            style: {
              position: containerPosition,
            },
            ref: refs.setFloating,
          })}
        >
          <div className={classNames('pds-bottom-sheet-content', className)}>
            <HandleArea
              onPointerDown={(event) => dragControls.start(event)}
              grab={canDragClose}
              closeDrag={canDragClose}
            />
            <Content closeDrag={canDragClose} maxHeight={maxHeight} {...contentProps}>
              {(title || subTitle) && (
                <Stack direction='column' gap={4}>
                  {title && (
                    <BaseText kind='Heading_20_Bold' color={semantic_colors.content.primary}>
                      {title}
                    </BaseText>
                  )}
                  {subTitle && (
                    <BaseText kind='Body_14_Medium' color={semantic_colors.content.secondary}>
                      {subTitle}
                    </BaseText>
                  )}
                </Stack>
              )}
              {typeof children === 'function' ? children({ close: () => handleClose() }) : children}
            </Content>
          </div>
        </ContentContainer>
      </MotionFloatingOverlay>
    );
  };

  return (
    <FloatingNode id={nodeId}>
      {noUsePortal ? (
        <AnimatePresence>
          {opened && (
            <FloatingFocusManager context={context} order={FLOATING_ORDER}>
              {renderToBottomSheet()}
            </FloatingFocusManager>
          )}
        </AnimatePresence>
      ) : (
        <AnimatePresence>
          {opened && (
            <FloatingPortal id={FLOATING_ROOT_ID}>
              <FloatingFocusManager context={context} order={FLOATING_ORDER}>
                {renderToBottomSheet()}
              </FloatingFocusManager>
            </FloatingPortal>
          )}
        </AnimatePresence>
      )}
    </FloatingNode>
  );
};

const BottomSheetWithContext = FloatingTreeContext(BottomSheetInner);
export const BottomSheet = (props: BottomSheetProps) => <BottomSheetWithContext {...props} />;

const ContentContainer = styled(motion.div)`
  position: fixed;
  width: 100%;
  right: 0;
  bottom: 0;
  left: 0;
`;

const Content = styled.div<BottomSheetContentProps & { closeDrag: boolean }>`
  background-color: ${semantic_colors.background.surface};
  padding: ${({ closeDrag }) => (closeDrag ? '0 16px 16px' : '16px')};
  overflow-x: hidden;
  word-break: break-all;
  ${shapes.shadow.high};
  ${({ maxHeight }) => maxHeight && `max-height: ${maxHeight - HANDLE_AREA_HEIGHT}px; overflow-y: auto;`};
  user-select: none;
  ${space}
`;

const HandleArea = styled.div<{ grab: boolean; closeDrag: boolean }>`
  position: relative;
  ${({ closeDrag }) => buildCSSWithLength('height', closeDrag ? HANDLE_AREA_HEIGHT : 12)};
  cursor: ${({ grab }) => (grab ? 'grab' : 'default')};
  background-color: ${semantic_colors.background.surface};
  border-top-left-radius: ${border_radius_styles_with_size.large}px;
  border-top-right-radius: ${border_radius_styles_with_size.large}px;

  &:after {
    ${absolute_center};
    content: '';
    display: ${({ closeDrag }) => (closeDrag ? 'block' : 'none')};
    width: 40px;
    height: 4px;
    border-radius: 100px;
    background-color: ${colors.gray250};
  }
`;
